// Stitch een NxN tile-grid van PDOK luchtfoto-tegels rond een geografisch
// centrum tot één canvas-texture. Wordt gebruikt als grondtextuur voor de
// 3D-scene (zie §2.3 / §5.1 van het ontwerp).
//
// We gebruiken EPSG:3857 Web Mercator-tegels. Bij zoom z is de wereld
// verdeeld in 2^z × 2^z tegels. De tegel-indices voor een lng/lat:
//   tx = floor((lng+180)/360 · 2^z)
//   ty = floor((1 − ln(tan(φ) + sec(φ))/π) / 2 · 2^z)
//
// Aan de noord/zuidkant van de bol komen de coördinaten dichter bij elkaar;
// in Hilversum (φ ≈ 52,2°) is een zoom-17-tegel ~190 m breed en ~120 m hoog
// in werkelijke meters. 5×5 tegels op zoom 17 dekken een ~950 m breed stuk —
// ruim voor onze 200 m schaalcase.

import * as THREE from "three";

const PDOK_TEMPLATE =
  "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg";
const TILE_SIZE = 256;

export interface PdokTextureResult {
  texture: THREE.CanvasTexture;
  /** Geografische bounds (WGS84) van de gecombineerde tile-grid. */
  bounds: { west: number; east: number; north: number; south: number };
  /** Pixel-resolutie van het stitched canvas (vierkant of niet, zie width/height). */
  width: number;
  height: number;
}

/**
 * Bouw een Three.js-texture uit een NxN grid van PDOK-tegels rond
 * (centerLat, centerLng) op de gegeven zoom. Geeft ook de geo-bounds zodat
 * de aanroeper het ground plane juist kan positioneren en schalen.
 */
export async function loadPdokGroundTexture(
  centerLat: number,
  centerLng: number,
  zoom: number,
  gridSize: number,
): Promise<PdokTextureResult> {
  const n = 1 << zoom;
  const xCenter = Math.floor(((centerLng + 180) / 360) * n);
  const yCenter = Math.floor(latToTileY(centerLat, n));
  const half = Math.floor(gridSize / 2);
  const startX = xCenter - half;
  const startY = yCenter - half;

  const canvas = document.createElement("canvas");
  canvas.width = gridSize * TILE_SIZE;
  canvas.height = gridSize * TILE_SIZE;
  const ctx = canvas.getContext("2d")!;
  // Lichtgrijze achtergrond als fallback voor tegels die niet laden.
  ctx.fillStyle = "#cccccc";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const fetches: Promise<void>[] = [];
  for (let dx = 0; dx < gridSize; dx++) {
    for (let dy = 0; dy < gridSize; dy++) {
      const tx = startX + dx;
      const ty = startY + dy;
      const url = PDOK_TEMPLATE.replace("{z}", String(zoom))
        .replace("{x}", String(tx))
        .replace("{y}", String(ty));
      fetches.push(
        loadImage(url)
          .then((img) => {
            ctx.drawImage(img, dx * TILE_SIZE, dy * TILE_SIZE);
          })
          .catch(() => {
            // Tegel kon niet geladen worden (bv. buiten NL); negeer.
          }),
      );
    }
  }
  await Promise.all(fetches);

  const west = tileXToLng(startX, n);
  const east = tileXToLng(startX + gridSize, n);
  const north = tileYToLat(startY, n);
  const south = tileYToLat(startY + gridSize, n);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  return {
    texture,
    bounds: { west, east, north, south },
    width: canvas.width,
    height: canvas.height,
  };
}

function latToTileY(lat: number, n: number): number {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n;
}

function tileXToLng(x: number, n: number): number {
  return (x / n) * 360 - 180;
}

function tileYToLat(y: number, n: number): number {
  const rad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  return (rad * 180) / Math.PI;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Tile load failed: ${url}`));
    img.src = url;
  });
}
